import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { Ticket, Scissors, CheckCircle, Star, AlertTriangle, RefreshCw } from 'lucide-react';
import { db } from '../../lib/firebase';
import { QueueTicket, Barber } from '../../types';

interface MetricCard {
 title: string;
 value: string | number;
 icon: ReactNode;
 trend?: string;
}

interface TicketsByStatus {
 waiting: number;
 waiting_overtime_approval: number;
 notified: number;
 arrived: number;
 in_service: number;
 finishing: number;
 completed: number;
 cancelled: number;
 expired: number;
}

interface BarberSummary {
 available: number;
 busy: number;
 on_break: number;
 offline: number;
}

interface Props {
 selectedFranchiseId: string;
 selectedBranchId: string;
}

const OverviewDashboard: React.FC<Props> = ({ selectedFranchiseId, selectedBranchId }) => {
 const { t } = useTranslation();
 const [loading, setLoading] = useState(true);
 const [activeTickets, setActiveTickets] = useState<QueueTicket[]>([]);
 const [ticketsByStatus, setTicketsByStatus] = useState<TicketsByStatus>({
 waiting: 0,
 waiting_overtime_approval: 0,
 notified: 0,
 arrived: 0,
 in_service: 0,
 finishing: 0,
 completed: 0,
 cancelled: 0,
 expired: 0,
 });
 const [barberSummary, setBarberSummary] = useState<BarberSummary>({
 available: 0,
 busy: 0,
 on_break: 0,
 offline: 0,
 });
 const [pendingVIPRequests, setPendingVIPRequests] = useState(0);
 const [todayStats, setTodayStats] = useState({
 completed: 0,
 noShows: 0,
 cancelled: 0,
 });
 const [alerts, setAlerts] = useState<{ key: string; params?: Record<string, unknown> }[]>([]);

 useEffect(() => {
 // Get start of today
 const todayStart = new Date();
 todayStart.setHours(0, 0, 0, 0);
 const todayStartTimestamp = Timestamp.fromDate(todayStart);

 // Subscribe to active tickets
 let activeTicketsQuery = query(
 collection(db, 'queues'),
 where('status', 'in', ['waiting', 'waiting_overtime_approval', 'notified', 'arrived', 'in_service'])
 );

 if (selectedFranchiseId !== 'all') {
 activeTicketsQuery = query(activeTicketsQuery, where('franchiseId', '==', selectedFranchiseId));
 }

 if (selectedBranchId !== 'all') {
 activeTicketsQuery = query(activeTicketsQuery, where('branchId', '==', selectedBranchId));
 }

 const unsubscribeActive = onSnapshot(activeTicketsQuery, (snapshot) => {
 const tickets = snapshot.docs.map((doc) => ({
 queueId: doc.id,
 ...doc.data(),
 })) as QueueTicket[];

 setActiveTickets(tickets);

 // Calculate tickets by status
 const statusCount: TicketsByStatus = {
 waiting: 0,
 waiting_overtime_approval: 0,
 notified: 0,
 arrived: 0,
 in_service: 0,
 finishing: 0,
 completed: 0,
 cancelled: 0,
 expired: 0,
 };

 tickets.forEach((ticket) => {
 statusCount[ticket.status]++;
 });

 setTicketsByStatus(statusCount);
 });

 // Subscribe to today's completed/cancelled/expired tickets
 let todayTicketsQuery = query(
 collection(db, 'queues'),
 where('createdAt', '>=', todayStartTimestamp)
 );

 if (selectedFranchiseId !== 'all') {
 todayTicketsQuery = query(todayTicketsQuery, where('franchiseId', '==', selectedFranchiseId));
 }

 if (selectedBranchId !== 'all') {
 todayTicketsQuery = query(todayTicketsQuery, where('branchId', '==', selectedBranchId));
 }

 const unsubscribeToday = onSnapshot(todayTicketsQuery, (snapshot) => {
 let completed = 0;
 let noShows = 0;
 let cancelled = 0;

 snapshot.docs.forEach((doc) => {
 const ticket = doc.data() as QueueTicket;
 if (ticket.status === 'completed') completed++;
 if (ticket.status === 'expired') noShows++;
 if (ticket.status === 'cancelled') cancelled++;
 });

 setTodayStats({ completed, noShows, cancelled });
 });

 // Subscribe to barber statuses from barbers collection
 let barbersQuery = query(collection(db, 'barbers'));

 if (selectedFranchiseId !== 'all') {
 barbersQuery = query(barbersQuery, where('franchiseId', '==', selectedFranchiseId));
 }

 if (selectedBranchId !== 'all') {
 barbersQuery = query(barbersQuery, where('branchId', '==', selectedBranchId));
 }

 const unsubscribeBarbers = onSnapshot(barbersQuery, (snapshot) => {
 const summary: BarberSummary = {
 available: 0,
 busy: 0,
 on_break: 0,
 offline: 0,
 };

 snapshot.docs.forEach((doc) => {
 const barber = doc.data() as Barber;
 if (!barber.isActive) {
 summary.offline++;
 } else if (barber.isAvailable) {
 summary.available++;
 } else {
 // isActive but not available = busy
 summary.busy++;
 }
 });

 setBarberSummary(summary);
 });

 // Subscribe to pending VIP appointment requests (citas pendientes de confirmación)
 let vipRequestsQuery = query(
 collection(db, 'appointments'),
 where('status', '==', 'pending')
 );

 if (selectedFranchiseId !== 'all') {
 vipRequestsQuery = query(vipRequestsQuery, where('franchiseId', '==', selectedFranchiseId));
 }

 if (selectedBranchId !== 'all') {
 vipRequestsQuery = query(vipRequestsQuery, where('branchId', '==', selectedBranchId));
 }

 const unsubscribeVIP = onSnapshot(vipRequestsQuery, (snapshot) => {
 setPendingVIPRequests(snapshot.size);
 });

 setLoading(false);

 return () => {
 unsubscribeActive();
 unsubscribeToday();
 unsubscribeBarbers();
 unsubscribeVIP();
 };
 }, [selectedFranchiseId, selectedBranchId]);

 // Separate useEffect for alerts - reacts to changes in activeTickets and barberSummary
 useEffect(() => {
 const newAlerts: { key: string; params?: Record<string, unknown> }[] = [];

 if (barberSummary.available === 0 && activeTickets.length > 0) {
 newAlerts.push({ key: 'adminOverview.alerts.noBarbers' });
 }

 if (ticketsByStatus.waiting_overtime_approval > 0) {
 newAlerts.push({
 key: 'adminOverview.alerts.pendingOvertime',
 params: { count: ticketsByStatus.waiting_overtime_approval }
 });
 }

 if (activeTickets.length > 20) {
 newAlerts.push({ key: 'adminOverview.alerts.queueSaturated' });
 }

 const longWaitTickets = activeTickets.filter(
 (ticket) => ticket.estimatedWaitTime > 60
 );
 if (longWaitTickets.length > 0) {
 newAlerts.push({ key: 'adminOverview.alerts.longWait', params: { count: longWaitTickets.length } });
 }

 setAlerts(newAlerts);
 }, [activeTickets, barberSummary, ticketsByStatus]);

 if (loading) {
 return (
 <div className="flex items-center justify-center h-96">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
 <p className="mt-4 text-ink-muted">{t('adminOverview.loading')}</p>
 </div>
 </div>
 );
 }

 const metrics: MetricCard[] = [
 {
 title: t('adminOverview.metrics.activeTickets'),
 value: activeTickets.length,
 icon: <Ticket className="w-7 h-7 text-ink-subtle" aria-hidden="true" />,
 },
 {
 title: t('adminOverview.metrics.availableBarbers'),
 value: barberSummary.available,
 icon: <Scissors className="w-7 h-7 text-ink-subtle" aria-hidden="true" />,
 },
 {
 title: t('adminOverview.metrics.completedToday'),
 value: todayStats.completed,
 icon: <CheckCircle className="w-7 h-7 text-ink-subtle" aria-hidden="true" />,
 },
 {
 title: t('adminOverview.metrics.pendingVIP'),
 value: pendingVIPRequests,
 icon: <Star className="w-7 h-7 text-ink-subtle" aria-hidden="true" />,
 },
 ];

 return (
 <div className="p-6 space-y-6">
 {/* Header */}
 <div>
 <h2 className="font-sans text-xl font-semibold tracking-tight text-ink">{t('adminOverview.title')}</h2>
 <p className="text-sm text-ink-muted mt-1">{t('adminOverview.subtitle')}</p>
 </div>

 {/* Alerts */}
 {alerts.length > 0 && (
 <div className="bg-danger/10 border border-danger/30 rounded-lg p-4">
 <div className="flex items-start">
 <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 shrink-0" aria-hidden="true" />
 <div className="flex-1">
 <h3 className="text-sm font-semibold text-ink mb-2">{t('adminOverview.alerts.title')}</h3>
 <ul className="space-y-1">
 {alerts.map((alert, index) => (
 <li key={index} className="text-sm text-ink-muted">
 • {String(t(alert.key, alert.params))}
 </li>
 ))}
 </ul>
 </div>
 </div>
 </div>
 )}

 {/* Metric Cards */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
 {metrics.map((metric) => (
 <div
 key={metric.title}
 className="bg-surface-2 border border-hairline rounded-lg p-6 transition-colors"
 >
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm font-medium text-ink-muted">{metric.title}</p>
 <p className="text-3xl font-semibold text-ink mt-2 tabular-nums">{metric.value}</p>
 </div>
 {metric.icon}
 </div>
 </div>
 ))}
 </div>

 {/* Status Distribution */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 {/* Tickets by Status */}
 <div className="bg-surface-2 border border-hairline rounded-lg p-6">
 <h3 className="font-sans text-lg font-semibold text-ink mb-4">{t('adminOverview.ticketsByStatus.title')}</h3>
 <div className="space-y-3">
 {Object.entries(ticketsByStatus).map(([status, count]) => (
 <div key={status} className="flex items-center justify-between">
 <span className="text-sm text-ink-muted">
 {t(`adminOverview.ticketsByStatus.${status}`)}
 </span>
 <div className="flex items-center space-x-2">
 <div className="w-32 bg-surface-3 rounded-full h-2">
 <div
 className={`h-2 rounded-full ${
 status === 'in_service'
 ? 'bg-info'
 : status === 'waiting_overtime_approval'
 ? 'bg-warning'
 : status === 'waiting' || status === 'notified' || status === 'arrived'
 ? 'bg-accent'
 : status === 'completed'
 ? 'bg-success'
 : 'bg-ink-subtle'
 }`}
 style={{
 width: `${
 activeTickets.length > 0 ? (count / activeTickets.length) * 100 : 0
 }%`,
 }}
 ></div>
 </div>
 <span className="text-sm font-semibold text-ink w-8 text-right tabular-nums">
 {count}
 </span>
 </div>
 </div>
 ))}
 </div>
 </div>

 {/* Barber Availability */}
 <div className="bg-surface-2 border border-hairline rounded-lg p-6">
 <h3 className="font-sans text-lg font-semibold text-ink mb-4">{t('adminOverview.barberAvailability.title')}</h3>
 <div className="space-y-3">
 {Object.entries(barberSummary).map(([status, count]) => (
 <div key={status} className="flex items-center justify-between">
 <span className="text-sm text-ink-muted">
 {t(`adminOverview.barberAvailability.${status}`)}
 </span>
 <div className="flex items-center space-x-2">
 <div className="w-32 bg-surface-3 rounded-full h-2">
 <div
 className={`h-2 rounded-full ${
 status === 'available'
 ? 'bg-success'
 : status === 'busy'
 ? 'bg-danger'
 : status === 'on_break'
 ? 'bg-warning'
 : 'bg-ink-subtle'
 }`}
 style={{
 width: `${
 Object.values(barberSummary).reduce((a, b) => a + b, 0) > 0
 ? (count /
 Object.values(barberSummary).reduce((a, b) => a + b, 0)) *
 100
 : 0
 }%`,
 }}
 ></div>
 </div>
 <span className="text-sm font-semibold text-ink w-8 text-right tabular-nums">
 {count}
 </span>
 </div>
 </div>
 ))}
 </div>
 </div>
 </div>

 {/* Today's Stats */}
 <div className="bg-surface-2 border border-hairline rounded-lg p-6">
 <h3 className="font-sans text-lg font-semibold text-ink mb-4">{t('adminOverview.todayPerformance.title')}</h3>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div className="text-center p-4 bg-surface border border-hairline rounded-lg">
 <p className="text-sm text-ink-muted">{t('adminOverview.todayPerformance.completedServices')}</p>
 <p className="text-3xl font-semibold text-success mt-2 tabular-nums">{todayStats.completed}</p>
 </div>
 <div className="text-center p-4 bg-surface border border-hairline rounded-lg">
 <p className="text-sm text-ink-muted">{t('adminOverview.todayPerformance.noShows')}</p>
 <p className="text-3xl font-semibold text-danger mt-2 tabular-nums">{todayStats.noShows}</p>
 </div>
 <div className="text-center p-4 bg-surface border border-hairline rounded-lg">
 <p className="text-sm text-ink-muted">{t('adminOverview.todayPerformance.cancellations')}</p>
 <p className="text-3xl font-semibold text-warning mt-2 tabular-nums">{todayStats.cancelled}</p>
 </div>
 </div>
 </div>

 {/* Auto-refresh indicator */}
 <div className="flex items-center justify-center gap-1.5 text-xs text-ink-subtle">
 <RefreshCw className="w-3 h-3" aria-hidden="true" />
 <p>{t('adminOverview.realTimeIndicator')}</p>
 </div>
 </div>
 );
};

export default OverviewDashboard;
